import {
  createFieldReadNestedTarget,
  type FieldReadDatabaseSnapshot
} from '@databases/field-read-modules';
import { type EntryDetailsReadSnapshot } from '@databases/field-read-modules/entry-details';
import { type EntryLinkDatabaseResourceInput } from '@databases/field-read-modules/entry-link';
import { type DefaultStore } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getSelectDatabaseDraft } from '@databases/redux/selectors/getSelectDatabaseDraft';
import { type Database } from '@databases/types/database';
import { getEntryDetailsTargetValues } from '@databases/utils/database/field-types/entryDetails';
import { getDatabaseEntryOptional } from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional
} from '@databases/utils/redux/databases/selectors';
import pLimit from 'p-limit';
import { type createRelatedReadSourceContext } from './related-read-source-context.ts';

type DetailsStore = Pick<DefaultStore, 'dispatch' | 'getState'>;
const selectDraft = getSelectDatabaseDraft(true);
/**
 * Reuses V1 relationship/default selection and retains no cache of visited entry targets.
 */
export function createEntryDetailsReadSource({
  store,
  sourceContext
}: {
  readonly store: DetailsStore;
  readonly sourceContext: ReturnType<typeof createRelatedReadSourceContext>;
}) {
  const limit = pLimit(8);
  const databases = new WeakMap<Database, FieldReadDatabaseSnapshot>();
  const readers = new Map<
    string,
    { state: ReturnType<DetailsStore['getState']>; value: EntryDetailsReadSnapshot }
  >();
  const key = (input: EntryLinkDatabaseResourceInput) =>
    JSON.stringify([input.contentId, input.databaseId, input.databaseSpaceKey]);
  const getSnapshot = (database: Database): FieldReadDatabaseSnapshot => {
    const cached = databases.get(database);
    if (cached) {
      return cached;
    }
    const snapshot: FieldReadDatabaseSnapshot = Object.freeze({
      key: `related:${database.id}`,
      fieldIds: database.fields,
      entryIds: database.entries,
      getField: (id: string) => database.fieldsById[id],
      getValue: ({
        entryId,
        fieldId
      }: {
        readonly entryId: string;
        readonly fieldId: string;
      }) => getDatabaseEntryOptional(database, entryId)?.values[fieldId],
      getDefaultValue: (id: string) => database.defaultEntry.values[id]
    });
    sourceContext.register(snapshot, database.contentId);
    databases.set(database, snapshot);
    return snapshot;
  };
  const getDatabaseSnapshot = (
    input: EntryLinkDatabaseResourceInput
  ): EntryDetailsReadSnapshot | undefined => {
    const state = store.getState();
    const cached = readers.get(key(input));
    if (cached?.state === state) {
      return cached.value;
    }
    const selected =
      input.databaseId && input.databaseSpaceKey
        ? selectDatabaseByIdOptional(state, {
            id: input.databaseId,
            spaceKey: input.databaseSpaceKey
          })
        : input.contentId
          ? selectDatabaseByContentIdOptional(state, { contentId: input.contentId })
          : undefined;
    if (!selected) {
      return undefined;
    }
    const value: EntryDetailsReadSnapshot = Object.freeze({
      getTargets({ database, entryId, field }) {
        const source = selectDatabaseDraftByContentId(state, {
          contentId: sourceContext.getContentId(database),
          includeDeleted: true
        });
        const entry = getDatabaseEntryOptional(source, entryId);
        return Object.freeze(
          getEntryDetailsTargetValues({ database: source, entry, field }, state, {
            selectDatabase: selectDraft
          }).map((target) => {
            const database = getSnapshot(
              selectDatabaseDraftByContentId(state, {
                contentId: target.database.contentId,
                includeDeleted: true
              })
            );
            const address = { entryId: target.entry.id, fieldId: target.field.id };
            return createFieldReadNestedTarget({
              database,
              field: target.field,
              address,
              storedValue: database.getValue(address),
              defaultValue: database.getDefaultValue(target.field.id)
            });
          })
        );
      }
    });
    readers.set(key(input), { state, value });
    return value;
  };
  return {
    getDatabaseSnapshot,
    getDatabaseResource: (input: EntryLinkDatabaseResourceInput) =>
      Object.freeze({
        key: `entry-details:${key(input)}`,
        load: (signal: AbortSignal) =>
          limit(async () => {
            if (signal.aborted) {
              return undefined;
            }
            if (input.contentId) {
              await store.dispatch(loadDatabaseByContentId(input.contentId));
            }
            return signal.aborted ? undefined : getDatabaseSnapshot(input);
          })
      })
  };
}
